import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Plus, Pencil, Trash2, Loader2, ToggleLeft, ToggleRight,
  ExternalLink, GripVertical, Tag, ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { ProviderLayout } from './ProviderLayout';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const PERIODS = [
  { value: 'monthly',  label: 'Monatlich' },
  { value: 'yearly',   label: 'Jährlich' },
  { value: 'one-time', label: 'Einmalig' },
];

const CATEGORIES = [
  { value: 'webspace',    label: 'Webspace' },
  { value: 'vserver',     label: 'VServer' },
  { value: 'dedi',        label: 'Dedicated Server' },
  { value: 'gameserver',  label: 'Gameserver' },
  { value: 'teamspeak',   label: 'TeamSpeak' },
  { value: 'discordbots', label: 'Discord Bots' },
  { value: 'musicbots',   label: 'Musicbots' },
  { value: 'domains',     label: 'Domains' },
  { value: 'other',       label: 'Sonstiges' },
];

const EMPTY = {
  name: '', description: '', price: 0, price_period: 'monthly',
  category: 'webspace', url: '', active: 1, sort_order: 0,
  cpu_type: '', cpu_cores: '', ram_type: '', ram_size: '',
  storage_type: '', storage_size: '', bandwidth: '', ddos_protection: '', traffic: '',
};

// ── NumberUnitInput ───────────────────────────────────────────────────────────
function parseNumUnit(val, defaultUnit) {
  if (!val) return { num: '', unit: defaultUnit };
  const m = String(val).match(/^([\d.,]+)\s*(.+)?$/);
  if (m) return { num: m[1], unit: m[2]?.trim() || defaultUnit };
  return { num: val, unit: defaultUnit };
}

function NumberUnitInput({ value, onChange, units, defaultUnit, placeholder = '0', testId }) {
  const { num, unit } = parseNumUnit(value, defaultUnit);
  const combine = (n, u) => (n ? `${n} ${u}` : '');
  return (
    <div className="flex rounded-lg overflow-hidden border border-slate-700/60 focus-within:border-cyan-500/70 transition-colors">
      <input type="number" min="0" step="any" value={num}
        onChange={e => onChange(combine(e.target.value, unit))}
        placeholder={placeholder} data-testid={testId}
        className="flex-1 min-w-0 px-3 py-2 bg-slate-800/50 text-sm text-white placeholder:text-slate-600 focus:outline-none" />
      <select value={units.includes(unit) ? unit : defaultUnit}
        onChange={e => onChange(combine(num, e.target.value))}
        className="px-2 py-2 bg-slate-700/60 text-xs text-slate-300 focus:outline-none border-l border-slate-700/60 cursor-pointer">
        {units.map(u => <option key={u} value={u}>{u}</option>)}
      </select>
    </div>
  );
}

// ── ProductForm (2-Tab: General + Specs) ─────────────────────────────────────
function ProductForm({ initial, onSave, onCancel, loading }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [tab, setTab]   = useState('general');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputCls = "w-full px-3 py-2.5 bg-slate-800/70 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 transition-colors";
  const labelCls = "text-xs font-medium text-slate-400 uppercase tracking-wider block mb-1";
  const specCount = ['cpu_type','cpu_cores','ram_type','ram_size','storage_type','storage_size','bandwidth','ddos_protection','traffic'].filter(k => form[k]).length;

  return (
    <div className="flex flex-col">
      <div className="flex border-b border-slate-800 mb-5 -mx-1">
        {[
          { id: 'general', label: 'Allgemein' },
          { id: 'specs',   label: `Spezifikationen${specCount > 0 ? ` (${specCount})` : ''}` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t.id ? 'text-cyan-400 border-cyan-400' : 'text-slate-500 border-transparent hover:text-slate-300'
            }`}>{t.label}</button>
        ))}
      </div>

      {tab === 'general' && (
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)}
              required data-testid="product-form-name" placeholder="z.B. Webspace Starter" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Preis (€)</label>
              <input type="number" step="0.01" min="0" value={form.price}
                onChange={e => set('price', parseFloat(e.target.value) || 0)}
                data-testid="product-form-price" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Abrechnung</label>
              <select value={form.price_period} onChange={e => set('price_period', e.target.value)}
                data-testid="product-form-period" className={inputCls + ' cursor-pointer'}>
                {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Kategorie</label>
            <select value={form.category} onChange={e => set('category', e.target.value)}
              data-testid="product-form-category" className={inputCls + ' cursor-pointer'}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Link (optional)</label>
            <input value={form.url} onChange={e => set('url', e.target.value)}
              placeholder="https://anbieter.de/produkt" data-testid="product-form-url" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Beschreibung</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)}
              rows={2} data-testid="product-form-desc"
              placeholder="Kurze Beschreibung des Produkts..."
              className={inputCls + ' resize-none'} />
          </div>
          <label className={`flex items-center gap-3 cursor-pointer select-none px-3 py-2.5 rounded-lg border transition-colors ${
            form.active ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-slate-800 border-slate-700 text-slate-400'
          }`}>
            <input type="checkbox" checked={!!form.active} onChange={e => set('active', e.target.checked ? 1 : 0)} className="sr-only" />
            {form.active ? <ToggleRight className="w-5 h-5 flex-shrink-0" /> : <ToggleLeft className="w-5 h-5 flex-shrink-0" />}
            <span className="text-sm font-medium">{form.active ? 'Aktiv – öffentlich sichtbar' : 'Inaktiv – nicht sichtbar'}</span>
          </label>
        </div>
      )}

      {tab === 'specs' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>CPU-Typ</label>
            <input value={form.cpu_type || ''} onChange={e => set('cpu_type', e.target.value)}
              placeholder="z.B. AMD Epyc 7331p" data-testid="product-form-cpu_type" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>CPU-Kerne</label>
            <NumberUnitInput value={form.cpu_cores} onChange={v => set('cpu_cores', v)}
              units={['vCore','vCores','Core','Cores','vCPU','Thread']}
              defaultUnit="vCore" placeholder="z.B. 4" testId="product-form-cpu_cores" />
          </div>
          <div>
            <label className={labelCls}>RAM-Typ</label>
            <input value={form.ram_type || ''} onChange={e => set('ram_type', e.target.value)}
              placeholder="z.B. DDR4 ECC" data-testid="product-form-ram_type" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>RAM-Größe</label>
            <NumberUnitInput value={form.ram_size} onChange={v => set('ram_size', v)}
              units={['MB','GB','TB']} defaultUnit="GB" placeholder="z.B. 4" testId="product-form-ram_size" />
          </div>
          <div>
            <label className={labelCls}>Speicher-Typ</label>
            <input value={form.storage_type || ''} onChange={e => set('storage_type', e.target.value)}
              placeholder="z.B. NVMe SSD" data-testid="product-form-storage_type" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Speicher-Größe</label>
            <NumberUnitInput value={form.storage_size} onChange={v => set('storage_size', v)}
              units={['MB','GB','TB']} defaultUnit="GB" placeholder="z.B. 40" testId="product-form-storage_size" />
          </div>
          <div>
            <label className={labelCls}>Anbindung</label>
            <input value={form.bandwidth || ''} onChange={e => set('bandwidth', e.target.value)}
              placeholder="z.B. 1 Gbit/s" data-testid="product-form-bandwidth" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Inkl. Traffic</label>
            <input value={form.traffic || ''} onChange={e => set('traffic', e.target.value)}
              placeholder="z.B. 20 TB / unlimitiert" data-testid="product-form-traffic" className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>DDoS-Schutz</label>
            <input value={form.ddos_protection || ''} onChange={e => set('ddos_protection', e.target.value)}
              placeholder="z.B. Voxility 1 Tbit/s" data-testid="product-form-ddos_protection" className={inputCls} />
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-5 mt-4 border-t border-slate-800">
        <button onClick={() => onSave(form)} disabled={!form.name || loading}
          data-testid="product-form-save"
          className="flex-1 py-2.5 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}Speichern
        </button>
        <button onClick={onCancel} data-testid="product-form-cancel"
          className="px-5 py-2.5 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm">
          Abbrechen
        </button>
      </div>
    </div>
  );
}

// ── Sortable table row ────────────────────────────────────────────────────────
function SortableProductRow({ p, idx, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  const cat = CATEGORIES.find(c => c.value === p.category);
  const periodLabel = { monthly: 'mtl.', yearly: 'jährl.', 'one-time': 'einmalig' }[p.price_period] || p.price_period;

  return (
    <tr ref={setNodeRef} style={style}
      data-testid={`product-row-${idx}`}
      className="border-b border-slate-800/60 hover:bg-white/[0.02] transition-colors group">

      {/* Drag handle — desktop only */}
      <td className="w-8 px-2 py-3 hidden md:table-cell">
        <button {...attributes} {...listeners}
          data-testid={`product-drag-${idx}`}
          className="cursor-grab active:cursor-grabbing text-slate-700 hover:text-slate-400 transition-colors p-1 rounded opacity-0 group-hover:opacity-100">
          <GripVertical className="w-4 h-4" />
        </button>
      </td>

      {/* Name + description */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-white text-sm">{p.name}</span>
          {!p.active && (
            <span className="px-1.5 py-0.5 rounded-full text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20">
              Inaktiv
            </span>
          )}
        </div>
        {p.description && (
          <p className="text-xs text-slate-500 mt-0.5 truncate max-w-xs">{p.description}</p>
        )}
      </td>

      {/* Price — hidden on small screens */}
      <td className="px-4 py-3 hidden sm:table-cell whitespace-nowrap">
        <span className="font-semibold text-cyan-400 text-sm">
          {p.price > 0 ? `${p.price.toFixed(2)} €` : 'kostenlos'}
        </span>
        <span className="text-xs text-slate-500 ml-1">{periodLabel}</span>
      </td>

      {/* Category — hidden on small screens */}
      <td className="px-4 py-3 hidden sm:table-cell">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-700/60 text-slate-400 border border-slate-700">
          <Tag className="w-2.5 h-2.5" />
          {cat?.label || p.category}
        </span>
      </td>

      {/* Active toggle */}
      <td className="px-4 py-3 hidden md:table-cell text-center">
        {p.active
          ? <ToggleRight className="w-5 h-5 text-green-400 mx-auto" />
          : <ToggleLeft  className="w-5 h-5 text-slate-600 mx-auto" />}
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 justify-end">
          {p.url && (
            <a href={p.url} target="_blank" rel="noopener noreferrer"
              className="p-1.5 text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors hidden sm:block">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          <button onClick={() => onEdit(p)} data-testid={`edit-product-${idx}`}
            className="p-1.5 text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors">
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(p)} data-testid={`delete-product-${idx}`}
            className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function ProviderProducts() {
  const { authHeaders, API } = useAdminAuth();
  const [products, setProducts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [reordering, setReordering] = useState(false);
  const [dialog, setDialog]       = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [page,    setPage]    = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [showAll, setShowAll] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/provider/products`, { headers: authHeaders() });
      setProducts(r.data);
    } finally { setLoading(false); }
  }, [API, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const totalPages      = Math.max(1, Math.ceil(products.length / perPage));
  const safePage        = Math.min(page, totalPages);
  const visibleProducts = showAll
    ? products
    : products.slice((safePage - 1) * perPage, safePage * perPage);

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = products.findIndex(p => p.id === active.id);
    const newIdx = products.findIndex(p => p.id === over.id);
    const reordered = arrayMove(products, oldIdx, newIdx);
    setProducts(reordered);
    setReordering(true);
    try {
      await axios.put(
        `${API}/provider/products/reorder`,
        { ids: reordered.map(p => p.id) },
        { headers: authHeaders() },
      );
    } catch {
      await load();
    } finally { setReordering(false); }
  };

  const save = async (form) => {
    setSaving(true);
    try {
      if (dialog.mode === 'add') {
        await axios.post(`${API}/provider/products`, form, { headers: authHeaders() });
      } else {
        await axios.put(`${API}/provider/products/${dialog.data.id}`, form, { headers: authHeaders() });
      }
      await load();
      setDialog(null);
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/provider/products/${id}`, { headers: authHeaders() });
    setDeleteConfirm(null);
    await load();
  };

  return (
    <ProviderLayout>
      <div className="p-4 sm:p-6 md:p-8 w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white">Produkte & Angebote</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {products.length} {products.length === 1 ? 'Produkt' : 'Produkte'}
              {reordering && (
                <span className="ml-2 text-cyan-400 inline-flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Reihenfolge wird gespeichert…
                </span>
              )}
            </p>
          </div>
          <button onClick={() => setDialog({ mode: 'add' })} data-testid="add-product-btn"
            className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors text-sm">
            <Plus className="w-4 h-4" /> Produkt hinzufügen
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-12 text-center">
            <p className="text-slate-400 text-sm mb-4">Noch keine Produkte. Füge dein erstes Angebot hinzu.</p>
            <button onClick={() => setDialog({ mode: 'add' })}
              className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/20 transition-colors">
              <Plus className="w-4 h-4" /> Erstes Produkt erstellen
            </button>
          </div>
        ) : (
          <div className="bg-[#0f172a] border border-slate-800 rounded-xl overflow-hidden">
            {products.length > 1 && (
              <div className="px-4 py-2 border-b border-slate-800/60 bg-slate-900/30 text-xs text-slate-600 hidden md:flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <GripVertical className="w-3.5 h-3.5" />
                  Zeilen ziehen zum Sortieren der Reihenfolge
                </span>
                <label className="flex items-center gap-1.5 cursor-pointer select-none text-slate-500 hover:text-slate-300 transition-colors">
                  <input
                    type="checkbox"
                    checked={showAll}
                    onChange={e => { setShowAll(e.target.checked); setPage(1); }}
                    className="w-3.5 h-3.5 rounded accent-cyan-500 cursor-pointer"
                  />
                  Alle anzeigen &amp; sortieren
                </label>
              </div>
            )}
            <div className="overflow-x-auto">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={visibleProducts.map(p => p.id)} strategy={verticalListSortingStrategy}>
                  <table className="w-full" data-testid="products-table">
                    <thead>
                      <tr className="border-b border-slate-800 text-xs font-semibold uppercase tracking-wider text-slate-400">
                        <th className="w-8 px-2 py-3 hidden md:table-cell"></th>
                        <th className="px-4 py-3 text-left">Produkt</th>
                        <th className="px-4 py-3 text-left hidden sm:table-cell">Preis</th>
                        <th className="px-4 py-3 text-left hidden sm:table-cell">Kategorie</th>
                        <th className="px-4 py-3 text-center hidden md:table-cell">Status</th>
                        <th className="px-4 py-3 text-right">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleProducts.map((p, i) => (
                        <SortableProductRow key={p.id} p={p} idx={i}
                          onEdit={p => setDialog({ mode: 'edit', data: p })}
                          onDelete={p => setDeleteConfirm(p)} />
                      ))}
                    </tbody>
                  </table>
                </SortableContext>
              </DndContext>
            </div>
            {/* Pagination Bar */}
            {!showAll && totalPages > 1 && (
              <div className="px-4 py-3 border-t border-slate-800/60 flex flex-wrap items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-3 text-slate-400">
                  <span>
                    {Math.min((safePage - 1) * perPage + 1, products.length)}–{Math.min(safePage * perPage, products.length)} von {products.length} Produkten
                  </span>
                  <select
                    value={perPage}
                    onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                    className="bg-slate-800 border border-slate-700 text-slate-300 rounded-lg px-2 py-1 text-xs cursor-pointer hover:border-slate-600 transition-colors">
                    {[10, 25, 50].map(n => (
                      <option key={n} value={n}>{n} pro Seite</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="p-1.5 rounded-lg border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
                    .reduce((acc, n, i, arr) => {
                      if (i > 0 && n - arr[i - 1] > 1) acc.push('…');
                      acc.push(n);
                      return acc;
                    }, [])
                    .map((n, i) =>
                      n === '…' ? (
                        <span key={`gap-${i}`} className="px-1 text-slate-600">…</span>
                      ) : (
                        <button key={n}
                          onClick={() => setPage(n)}
                          className={`min-w-[28px] h-7 rounded-lg text-xs font-medium border transition-colors ${
                            safePage === n
                              ? 'bg-cyan-500 border-cyan-500 text-slate-950'
                              : 'border-slate-700 text-slate-400 hover:text-white hover:border-slate-500'
                          }`}>
                          {n}
                        </button>
                      )
                    )}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="p-1.5 rounded-lg border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit/Add dialog */}
      <Dialog open={!!dialog} onOpenChange={open => !open && setDialog(null)}>
        <DialogContent className="bg-[#0f172a] border-slate-700 text-white w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === 'add' ? 'Neues Produkt' : 'Produkt bearbeiten'}</DialogTitle>
          </DialogHeader>
          {dialog && <ProductForm initial={dialog.data} onSave={save} onCancel={() => setDialog(null)} loading={saving} />}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={open => !open && setDeleteConfirm(null)}>
        <DialogContent className="bg-[#0f172a] border-slate-700 text-white max-w-sm">
          <DialogHeader><DialogTitle>Produkt löschen?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">
            <span className="text-white font-medium">{deleteConfirm?.name}</span> wird unwiderruflich gelöscht.
          </p>
          <div className="flex gap-2 mt-2">
            <button onClick={() => remove(deleteConfirm?.id)} data-testid="confirm-delete-product"
              className="flex-1 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 text-sm transition-colors">
              Löschen
            </button>
            <button onClick={() => setDeleteConfirm(null)}
              className="flex-1 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 text-sm transition-colors">
              Abbrechen
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </ProviderLayout>
  );
}
